import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { ASSETS, NAP, SERVICES, AREAS, HIGHLIGHTED_AREAS, LOCAL_AREAS, BOOK_URL } from "../lib/data";
import { TID } from "../lib/testIds";
import { trackBookClick } from "../lib/analytics";

export default function Footer() {
  return (
    <footer className="bg-[#050505] border-t border-[#262626] mt-0">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div className="md:col-span-1 flex flex-col items-start">
          {/* Logo on transparent background, centered with column */}
          <div
            className="h-20 lg:h-24 w-[220px] lg:w-[280px] bg-no-repeat bg-left bg-contain mb-5 mx-auto md:mx-0"
            style={{
              backgroundImage: `url("${ASSETS.logo}")`,
              transform: "scale(1.6)",
              transformOrigin: "left center",
              filter: "brightness(1.05) drop-shadow(0 0 10px rgba(220,38,38,0.15))",
            }}
            role="img"
            aria-label="Act Fast Appliance Repair"
            data-testid="footer-logo"
          />
          <p className="text-neutral-400 text-sm leading-relaxed">
            Family-run appliance repair specialists serving Chicago and 34 surrounding communities for
            26+ years. Licensed, insured, and factory-trained.
          </p>
          <div className="flex flex-col gap-3 mt-6 text-sm">
            <a href={NAP.phoneTel} className="flex items-center gap-2 text-white hover:text-[#DC2626] transition-colors" data-testid={TID.footerCall}>
              <Phone size={16} className="text-[#DC2626]" /> {NAP.phoneDisplay}
            </a>
            <a href={NAP.emailLink} className="flex items-center gap-2 text-white hover:text-[#DC2626] transition-colors" data-testid={TID.footerEmail}>
              <Mail size={16} className="text-[#DC2626]" /> {NAP.email}
            </a>
            <div className="flex items-start gap-2 text-neutral-400">
              <MapPin size={16} className="text-[#DC2626] mt-0.5 shrink-0" />
              <span>{NAP.addressLine1}, {NAP.city}, {NAP.state} {NAP.zip}</span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="af-eyebrow mb-5">Services</h4>
          <ul className="space-y-3 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  {s.short}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="af-eyebrow mb-5">Featured Areas</h4>
          <ul className="space-y-3 text-sm">
            {LOCAL_AREAS.filter((a) => HIGHLIGHTED_AREAS.includes(a.city)).map((a) => (
              <li key={a.slug}>
                <Link
                  to={`/areas/${a.slug}`}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  {a.name}
                </Link>
              </li>
            ))}
            <li>
              <a href="#footer-areas-grid" className="text-[#DC2626] hover:text-white">All 34 Communities ↓</a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="af-eyebrow mb-5">Business Hours</h4>
          <ul className="space-y-3 text-sm">
            {NAP.hours.map((h) => (
              <li key={h.day} className="flex items-start gap-2 text-neutral-400">
                <Clock size={16} className="text-[#DC2626] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white">{h.day}</div>
                  <div className="text-xs">{h.time}</div>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/contact" className="af-btn-outline mt-6 w-full justify-center text-xs">
            Contact / Get a Quote
          </Link>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackBookClick("footer")}
            className="af-btn-primary mt-3 w-full justify-center text-xs"
            data-testid="footer-book-online"
          >
            Book Service Online
          </a>
        </div>
      </div>

      {/* Areas We Serve — 34 communities for local SEO crawlability */}
      <div className="border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="af-eyebrow mb-2">Areas We Serve</p>
              <h3 className="text-2xl lg:text-3xl font-bold font-display">
                <span className="text-[#DC2626]">34 Chicagoland</span> Communities
              </h3>
            </div>
            <Link
              to="/contact"
              className="text-xs uppercase tracking-widest text-neutral-400 hover:text-[#DC2626] af-link-underline"
            >
              Don't see your city? Confirm coverage →
            </Link>
          </div>
          <div
            id="footer-areas-grid"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-2"
            data-testid="footer-areas-grid"
          >
            {AREAS.map((area) => {
              const localData = LOCAL_AREAS.find((l) => l.city === area);
              const isHighlighted = HIGHLIGHTED_AREAS.includes(area);
              if (!localData) return null;
              const linkClass = isHighlighted
                ? "text-sm text-white font-semibold hover:text-[#DC2626] transition-colors py-1"
                : "text-sm text-neutral-400 hover:text-white transition-colors py-1";
              return (
                <Link
                  key={area}
                  to={`/areas/${localData.slug}`}
                  data-testid={`footer-area-${localData.slug}`}
                  className={linkClass}
                  title={`${area} appliance repair`}
                >
                  {isHighlighted ? `${area} →` : area}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="border-t border-[#262626] pb-24 lg:pb-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row justify-between gap-4 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Act Fast Appliance Repair. All rights reserved.</div>
          <div className="flex gap-6 uppercase tracking-wider">
            <span>Licensed & Insured</span>
            <span>Parts & Labor Warranty</span>
            <span>Family-Run since 1999</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
